import styles from './GrantAccess.module.css';
import { ReactComponent as Close } from '../ico/close.svg';
import { Button } from '../../UI/Button/Button';
import Select from '../../UI/Select/Select';
import { observer } from 'mobx-react';
import { useStores } from '../../../hooks/useStore';
import { useApi } from '../../../hooks/useApi';
import { useEffect, useState } from 'react';
import { AppUser } from '../../../models/appUser';
import { GrantWithDetails } from '../../../models/accessGrant';
import { CoursesModel } from '../../../models/courses';

interface PopupProps {
  user: AppUser;
  // eslint-disable-next-line no-unused-vars
  onClose: () => void;
}

const formatDate = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleDateString('ru-RU');
};

const GrantAccessPopUP: React.FC<PopupProps> = observer(({ user, onClose }) => {
  const store = useStores();
  const api = useApi();

  const [courses, setCourses] = useState<CoursesModel[]>([]);
  const [grants, setGrants] = useState<GrantWithDetails[]>([]);

  const [courseId, setCourseId] = useState<string>('');
  const [startsAt, setStartsAt] = useState<string>('');
  const [endsAt, setEndsAt] = useState<string>('');

  useEffect(() => {
    loadCourses();
    loadGrants();
  }, [user.id]);

  const loadCourses = async () => {
    try {
      const resp = await api.courses.get(0, 1000);
      if (resp.body) {
        setCourses(resp.body);
      }
    } catch (e) {
      store.notificationsStore.setError('Ошибка загрузки курсов');
    }
  };

  const loadGrants = async () => {
    try {
      store.notificationsStore.showPreloader();
      const resp = await api.accessGrants.listUserGrants(user.id);
      if (resp.body) {
        setGrants(resp.body);
      } else {
        setGrants([]);
      }
    } catch (e) {
      store.notificationsStore.setError('Ошибка загрузки доступов');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const courseTitle = (id: string | null): string => {
    const found = courses.find((c) => c.id === id);
    return found?.title ? found.title : 'Курс';
  };

  const handleOverlayClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const grant = async () => {
    if (!courseId) {
      return;
    }
    try {
      store.notificationsStore.showPreloader();
      await api.accessGrants.grant(courseId, {
        user_id: user.id,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      });
      store.notificationsStore.setNotifications('Доступ выдан');
      setCourseId('');
      setStartsAt('');
      setEndsAt('');
      await loadGrants();
    } catch (e) {
      store.notificationsStore.setError('Ошибка при выдаче доступа');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const toggleActive = async (item: GrantWithDetails) => {
    try {
      store.notificationsStore.showPreloader();
      await api.accessGrants.updateGrant(item.id, {
        is_active: !item.is_active,
      });
      store.notificationsStore.setNotifications(
        item.is_active ? 'Доступ отозван' : 'Доступ возобновлён',
      );
      await loadGrants();
    } catch (e) {
      store.notificationsStore.setError('Ошибка при изменении доступа');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const remove = async (item: GrantWithDetails) => {
    try {
      store.notificationsStore.showPreloader();
      await api.accessGrants.deleteGrant(item.id);
      store.notificationsStore.setNotifications('Доступ удалён');
      await loadGrants();
    } catch (e) {
      store.notificationsStore.setError('Ошибка при удалении доступа');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const courseOptions = [
    { id: '', name: 'Выберите курс' },
    ...courses
      .filter((c) => !!c.id)
      .map((c) => ({ id: c.id as string, name: c.title ?? 'Без названия' })),
  ];

  return (
    <div className={styles.container} onClick={handleOverlayClick}>
      <div className={styles.form}>
        <Close className={styles.close} onClick={onClose} />
        <h3>Доступы к курсам</h3>
        <div className={styles.userInfo}>
          {user.name ? user.name : 'Имя не указано'}
          <br />
          {user.email}
        </div>
        <div className={styles.line} />

        <div className={styles.body}>
          <div className={styles.sectionTitle}>Текущие доступы</div>

          {grants.length === 0 && (
            <div className={styles.empty}>Доступов пока нет</div>
          )}

          {grants.length > 0 && (
            <div className={styles.grantList}>
              {grants.map((item) => (
                <div key={item.id} className={styles.grantItem}>
                  <div>
                    <div className={styles.grantCourse}>
                      {item.course_title
                        ? item.course_title
                        : courseTitle(item.course_id)}
                    </div>
                    <div className={styles.grantMeta}>
                      {formatDate(item.starts_at)} — {formatDate(item.ends_at)}
                    </div>
                  </div>
                  <div className={styles.grantActions}>
                    <span
                      className={`${styles.status} ${
                        item.is_active ? styles.active : styles.inactive
                      }`}
                    >
                      {item.is_active ? 'активен' : 'отозван'}
                    </span>
                    <span
                      className={styles.actionLink}
                      onClick={() => toggleActive(item)}
                    >
                      {item.is_active ? 'Отозвать' : 'Возобновить'}
                    </span>
                    <span
                      className={styles.actionDanger}
                      onClick={() => remove(item)}
                    >
                      Удалить
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={styles.sectionTitle}>Выдать доступ к курсу</div>

          <Select
            listCategories={courseOptions}
            selectedValue={courseId}
            onChange={(e) => setCourseId(e.currentTarget.value)}
          />

          <div className={styles.dates}>
            <div className={styles.dateField}>
              <label>Начало (необязательно)</label>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div className={styles.dateField}>
              <label>Окончание (необязательно)</label>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
          </div>

          <Button
            appearance="auth"
            disabled={!courseId}
            style={{ maxWidth: 320, marginBottom: 25 }}
            onClick={grant}
          >
            Выдать доступ
          </Button>
        </div>
      </div>
    </div>
  );
});

export default GrantAccessPopUP;
